/**
 * Runtime detection for the Capacitor shell.
 *
 * The same Vite bundle runs in three environments:
 *   - a normal web browser (the PWA at underlings.com)
 *   - the iOS Capacitor shell (WKWebView)
 *   - the Android Capacitor shell (Chrome WebView)
 *
 * Use `isNative()` to branch device-API code paths. The Capacitor JS shims
 * report `false` when no native bridge is present, so importing them on web
 * is safe — but for tree-shaking and clarity, gate native imports behind
 * `isNative()`.
 */

import { Capacitor } from '@capacitor/core';

export type Platform = 'web' | 'ios' | 'android';

export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

export function platform(): Platform {
  const p = Capacitor.getPlatform();
  if (p === 'ios' || p === 'android') return p;
  return 'web';
}

/**
 * Fire `cb` whenever the Capacitor app transitions from background to active
 * (foregrounded). Returns an unsubscribe. Web no-op — falls back to the
 * standard `visibilitychange` event so the same call site works in browsers.
 *
 * Used by the OTA UpdateGate to re-check the manifest each time the user
 * comes back to the app.
 */
export function onForegrounded(cb: () => void): () => void {
  if (isNative()) {
    let handle: { remove: () => Promise<void> } | null = null;
    let cancelled = false;
    void (async () => {
      const { App } = await import('@capacitor/app');
      if (cancelled) return;
      handle = await App.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => {
        if (isActive) cb();
      });
    })();
    return () => {
      cancelled = true;
      void handle?.remove().catch(() => {});
    };
  }

  // Web fallback — `visibilitychange` fires on tab focus too, but that's a
  // reasonable proxy for "user came back to the app".
  if (typeof document === 'undefined') return () => {};
  const handler = () => {
    if (document.visibilityState === 'visible') cb();
  };
  document.addEventListener('visibilitychange', handler);
  return () => document.removeEventListener('visibilitychange', handler);
}

/**
 * Terminate the app cleanly. Used by the OTA flow on Android: a staged
 * bundle only takes effect on the next cold start, and `App.exitApp()`
 * is the only way to trigger that cold start from JS (Android doesn't
 * permit programmatic activity restart). The user then re-launches from
 * the home-screen icon and lands in the fresh bundle via the stub's
 * fast-load path.
 *
 * No-op on iOS (Apple HIG forbids apps from terminating themselves, and
 * the Capacitor plugin honours that) and on web. Callers should always
 * present a clear user-facing action that explains the imminent exit.
 */
export async function exitApp(): Promise<void> {
  if (!isNative()) return;
  const { App } = await import('@capacitor/app');
  await App.exitApp().catch(() => {});
}

/**
 * Configure the native status bar to play nicely with our React header.
 *
 * Capacitor 8 made Android WebViews edge-to-edge by default — the WebView
 * extends behind the system status bar, the same way it always did on iOS.
 * For that to look right, CSS needs `env(safe-area-inset-top)` to report
 * the status bar height so the header can pad itself down. On iOS WKWebView
 * dispatches `WindowInsets` to the WebView automatically; on Android Cap 8
 * does NOT (yet) wire WindowInsetsCompat through to CSS env() values, so
 * the header ends up overlapping the status bar and clock/battery icons.
 *
 * Fix: call StatusBar.setOverlaysWebView({ overlay: false }) on Android.
 * That restores the Cap-6-era "status bar reserves its own space" layout
 * without us needing to install the new @capacitor/system-bars plugin
 * just to ferry insets into CSS. The status bar gets a solid background
 * matching whatever colour we set on the WebView root.
 *
 * No-op on iOS (Info.plist controls status bar behaviour there — our
 * apple-mobile-web-app-status-bar-style: black-translucent setting plus
 * the WKWebView's automatic safe-area dispatch already does the right
 * thing in CSS).
 *
 * Call once at app boot, before React mounts, so the layout settles
 * before the first paint.
 */
export async function configureStatusBar(): Promise<void> {
  if (platform() !== 'android') return;
  try {
    const { StatusBar } = await import('@capacitor/status-bar');
    await StatusBar.setOverlaysWebView({ overlay: false });
  } catch {
    // Plugin missing in this build — harmless. Edge-to-edge stays on
    // and the user might see the overlap until we ship a fix, but it
    // won't crash the app.
  }
}

/**
 * Subscribe to deep-link opens. Fires whenever the OS hands a URL to
 * the app via a Universal Link (iOS) / App Link (Android) tap, custom
 * URL scheme, or `App.openUrl()`.
 *
 * The handler receives the URL's PATH+SEARCH only — the scheme/host are
 * stripped, so the same handler works regardless of which of our claimed
 * hosts (app./m./desktop. underlings.com) the user tapped. e.g.
 *
 *   https://mobile.underlings.com/claim/abc123?ref=email
 *      → cb('/claim/abc123?ref=email')
 *
 * Caller is expected to do `navigate(path)` (React Router) to route it.
 *
 * Returns an unsubscribe. No-op on web (we use the browser's address
 * bar / hash routing directly there). Safe to call on web — returns a
 * no-op unsubscribe.
 */
export function onDeepLink(cb: (pathWithSearch: string) => void): () => void {
  if (!isNative()) return () => {};
  let handle: { remove: () => Promise<void> } | null = null;
  let cancelled = false;
  void (async () => {
    const { App } = await import('@capacitor/app');
    if (cancelled) return;
    handle = await App.addListener('appUrlOpen', ({ url }: { url: string }) => {
      try {
        const u = new URL(url);
        // Strip scheme + host; pass the rest through so the React Router
        // sees the same path it would render at the equivalent web URL.
        cb(u.pathname + u.search + u.hash);
      } catch {
        // Malformed URL — ignore. Shouldn't happen in practice since the
        // OS only dispatches URLs that matched our intent filter / AASA.
      }
    });
  })();
  return () => {
    cancelled = true;
    void handle?.remove().catch(() => {});
  };
}

/**
 * If the app was launched FROM a deep link (cold start, link tapped
 * in another app), return the launch URL's path+search. Otherwise null.
 *
 * Important: on cold-start, `appUrlOpen` does fire AFTER React mounts,
 * but there can be a race where React's first navigate happens before
 * the listener attaches. Checking this synchronously at mount lets us
 * route to the correct path on first paint without a flash.
 *
 * Consume-once: Capacitor's `App.getLaunchUrl` keeps returning the
 * stored launch URL for the lifetime of the process, so a caller that
 * legitimately re-mounts (e.g. an effect re-running because navigate
 * isn't stable) would re-navigate to the launch target on every back.
 * We latch the result at module scope so only the first caller in this
 * session sees it.
 *
 * No-op on web — returns null.
 */
let launchUrlConsumed = false;
export async function getLaunchDeepLink(): Promise<string | null> {
  if (!isNative() || launchUrlConsumed) return null;
  launchUrlConsumed = true;
  try {
    const { App } = await import('@capacitor/app');
    const state = await App.getLaunchUrl();
    if (!state?.url) return null;
    const u = new URL(state.url);
    return u.pathname + u.search + u.hash;
  } catch {
    return null;
  }
}
